// StrategyAlertTab.jsx
// DAS-3341: the "Strategy Alert" tab in the Alerts modal — the strategy
// review lights (DAS-3331/DAS-3340) and the owner's dated notes, plus the
// life events the owner records (DAS-3338). The tab reads its own table
// (Strategy_Check / Strategy_Life_Events), outside the alert/clause machinery
// (decided on DAS-3331).
//
// Reads GET /api/strategy/check and GET /api/strategy/life_events (both
// return a plain array, caller's own rows only). Notes ride
// POST /api/strategy/check/<id>/note; life events ride
// POST/DELETE /api/strategy/life_events[/<id>] (DAS-3338).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Spin, Empty, Button, Input, Alert, message } from 'antd';
import api from '../functions/api';

const { Text, Paragraph } = Typography;

const LIGHT_COLORS = { green: '#2e7d32', amber: '#f5a623', red: '#c62828', unknown: '#bbb' };

// DAS-3331: plain-word names for each trigger key.
const TRIGGER_LABELS = {
  passmark_year: 'Pass mark last year',
  buying_power: 'Buying power',
  living_costs: 'Living costs',
  swing_band: 'Risk band',
  cpb_forecast: 'Inflation forecast',
  cash_rate: 'Savings rate',            // DAS-3355
  life_event: 'Life event',
};

// Allow-listed kinds (DAS-3338's EVENT_TYPES) with plain-word labels.
const EVENT_TYPE_LABELS = {
  apartment_sold: 'Apartment sold',
  pension_started: 'Pension started',
  loan_paid_out: 'Loan paid out',
  gift: 'Gift',
  large_expense: 'Large expense',
  other: 'Other',
};
const EVENT_TYPES = Object.keys(EVENT_TYPE_LABELS);

const asArray = (data) => (Array.isArray(data) ? data : []);

const fmtAmount = (v) => (v == null ? '' : `€${Number(v).toLocaleString()}`);

// The API's DATE columns (check_date, owner_note_date, event_date) go through
// Flask's jsonify as either an ISO string or (if a route forgets .isoformat())
// Flask's RFC-1123 date serialization — "Sat, 01 Nov 2026 00:00:00 GMT". Parse
// with Date() rather than slicing, so either shape renders as a plain date.
const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10);
};

const Dot = ({ color, title }) => (
  <span
    title={title}
    style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: color }}
  />
);

const StrategyAlertTab = ({ onUnseenChange }) => {
  const [checkRows, setCheckRows] = useState(null); // null = loading
  const [lifeEvents, setLifeEvents] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [lifeEventsError, setLifeEventsError] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({}); // id -> draft text
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [eventForm, setEventForm] = useState({ event_date: '', event_type: 'other', amount_eur: '', note: '' });
  const [addingEvent, setAddingEvent] = useState(false);

  const loadChecks = useCallback(() => {
    api.get('/strategy/check')
      .then((res) => { setCheckRows(asArray(res.data)); setCheckError(null); })
      .catch((err) => { setCheckRows([]); setCheckError(err?.response?.data?.error || err.message); });
  }, []);

  const loadLifeEvents = useCallback(() => {
    api.get('/strategy/life_events')
      .then((res) => { setLifeEvents(asArray(res.data)); setLifeEventsError(null); })
      .catch((err) => { setLifeEvents([]); setLifeEventsError(err?.response?.data?.error || err.message); });
  }, []);

  useEffect(() => { loadChecks(); }, [loadChecks]);
  useEffect(() => { loadLifeEvents(); }, [loadLifeEvents]);

  // Group check rows by check_date, newest first (the backend already orders
  // the rows that way), so the first group is the current/latest month.
  const grouped = useMemo(() => {
    const byDate = new Map();
    for (const r of checkRows || []) {
      if (!byDate.has(r.check_date)) byDate.set(r.check_date, []);
      byDate.get(r.check_date).push(r);
    }
    return Array.from(byDate.entries());
  }, [checkRows]);

  const [latestDate, latestRows] = grouped[0] || [null, []];
  const historyGroups = grouped.slice(1);

  useEffect(() => {
    if (!onUnseenChange) return;
    onUnseenChange(latestRows.some((r) => r.light === 'red' && !r.owner_note));
  }, [latestRows, onUnseenChange]);

  const saveNote = async (id) => {
    setSavingNoteId(id);
    try {
      await api.post(`/strategy/check/${id}/note`, { note: noteDrafts[id] || '' });
      loadChecks();
    } catch (err) {
      message.error('Could not save note: ' + (err?.response?.data?.error || err.message));
    } finally {
      setSavingNoteId(null);
    }
  };

  const submitLifeEvent = async () => {
    if (!eventForm.event_date || !eventForm.event_type) return;
    setAddingEvent(true);
    try {
      await api.post('/strategy/life_events', {
        event_date: eventForm.event_date,
        event_type: eventForm.event_type,
        amount_eur: eventForm.amount_eur === '' ? null : Number(eventForm.amount_eur),
        note: eventForm.note,
      });
      setEventForm({ event_date: '', event_type: 'other', amount_eur: '', note: '' });
      loadLifeEvents();
    } catch (err) {
      message.error('Could not save life event: ' + (err?.response?.data?.error || err.message));
    } finally {
      setAddingEvent(false);
    }
  };

  const deleteLifeEvent = async (id) => {
    try {
      await api.delete(`/strategy/life_events/${id}`);
      loadLifeEvents();
    } catch (err) {
      message.error('Could not delete the life event: ' + (err?.response?.data?.error || err.message));
    }
  };

  if (checkRows === null || lifeEvents === null) {
    return (
      <div data-testid="strategy-alert-tab">
        <Spin style={{ display: 'block', margin: '40px auto' }} />
      </div>
    );
  }

  const lightBlock = (row) => {
    const color = LIGHT_COLORS[row.light] || LIGHT_COLORS.unknown;
    const needsNote = (row.light === 'red' || row.light === 'amber') && !row.owner_note;
    return (
      <div key={row.id} data-testid={`strategy-light-${row.trigger_key}`} style={{ minWidth: 160, maxWidth: 220 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Dot color={color} title={row.light} />
          <Text strong style={{ fontSize: 13 }}>{TRIGGER_LABELS[row.trigger_key] || row.trigger_key}</Text>
        </div>
        {row.detail && <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{row.detail}</Text>}
        {row.strategy_section && (
          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>section: {row.strategy_section}</Text>
        )}
        {row.owner_note ? (
          <div data-testid={`strategy-note-saved-${row.id}`} style={{ marginTop: 4 }}>
            <Text italic style={{ fontSize: 12 }}>{row.owner_note}</Text>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{fmtDate(row.owner_note_date)}</Text>
          </div>
        ) : needsNote ? (
          <div style={{ marginTop: 4 }}>
            <Input.TextArea
              data-testid={`strategy-note-input-${row.id}`}
              rows={2}
              value={noteDrafts[row.id] || ''}
              onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
            />
            <Button
              size="small"
              style={{ marginTop: 4 }}
              loading={savingNoteId === row.id}
              data-testid={`strategy-note-save-${row.id}`}
              onClick={() => saveNote(row.id)}
            >
              Save
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div data-testid="strategy-alert-tab">
      {checkError ? (
        <Alert
          type="error"
          data-testid="strategy-check-error"
          message="Could not load the strategy lights"
          description={checkError}
          style={{ marginBottom: 16 }}
        />
      ) : latestRows.length === 0 ? (
        <Empty description="No strategy check yet — the first runs on the next morning run." />
      ) : (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            The strategy lights for {fmtDate(latestDate)}. A red or amber light without a note asks for one below it.
          </Paragraph>
          <div data-testid="strategy-lights" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            {latestRows.map(lightBlock)}
          </div>
          {historyGroups.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Text strong>Previous months</Text>
              {historyGroups.map(([date, rows]) => (
                <div
                  key={date}
                  data-testid="strategy-month-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}
                >
                  <Text type="secondary" style={{ width: 90, fontSize: 12 }}>{fmtDate(date)}</Text>
                  <span style={{ display: 'flex', gap: 6 }}>
                    {rows.map((r) => (
                      <Dot
                        key={r.id}
                        color={LIGHT_COLORS[r.light] || LIGHT_COLORS.unknown}
                        title={`${TRIGGER_LABELS[r.trigger_key] || r.trigger_key}: ${r.detail || ''}`}
                      />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div data-testid="strategy-life-events">
        <Text strong>Life events</Text>
        {lifeEventsError ? (
          <Alert
            type="error"
            data-testid="strategy-life-events-error"
            message="Could not load life events"
            description={lifeEventsError}
            style={{ marginTop: 6 }}
          />
        ) : lifeEvents.length === 0 ? (
          <Paragraph type="secondary" style={{ marginTop: 6, fontSize: 13 }}>No life events recorded yet.</Paragraph>
        ) : (
          lifeEvents.map((ev) => (
            <div
              key={ev.id}
              data-testid="strategy-life-event-row"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}
            >
              <Text type="secondary" style={{ width: 90, fontSize: 12 }}>{fmtDate(ev.event_date)}</Text>
              <Text style={{ minWidth: 130 }}>{EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}</Text>
              <Text style={{ minWidth: 90 }}>{fmtAmount(ev.amount_eur)}</Text>
              <Text type="secondary" style={{ flex: 1, fontSize: 12 }}>{ev.note}</Text>
              <Button
                size="small"
                danger
                data-testid={`strategy-life-event-delete-${ev.id}`}
                onClick={() => deleteLifeEvent(ev.id)}
              >
                Delete
              </Button>
            </div>
          ))
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <input
            type="date"
            data-testid="strategy-life-event-date"
            value={eventForm.event_date}
            onChange={(e) => setEventForm((prev) => ({ ...prev, event_date: e.target.value }))}
          />
          <select
            data-testid="strategy-life-event-type"
            value={eventForm.event_type}
            onChange={(e) => setEventForm((prev) => ({ ...prev, event_type: e.target.value }))}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Amount (EUR)"
            data-testid="strategy-life-event-amount"
            value={eventForm.amount_eur}
            onChange={(e) => setEventForm((prev) => ({ ...prev, amount_eur: e.target.value }))}
          />
          <Input
            placeholder="Note"
            data-testid="strategy-life-event-note"
            style={{ width: 220 }}
            value={eventForm.note}
            onChange={(e) => setEventForm((prev) => ({ ...prev, note: e.target.value }))}
          />
          <Button
            type="primary"
            size="small"
            loading={addingEvent}
            data-testid="strategy-life-event-add"
            onClick={submitLifeEvent}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StrategyAlertTab;
